import { useContext, useEffect,useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import ArtworkIcon from "src/assets/all-icons/artwork-icon.jsx";
import BoardIcon from "src/assets/all-icons/board-icon.jsx";
import CollectionIcon from "src/assets/all-icons/collection-icon.jsx";
import CommunityIcon from "src/assets/all-icons/community-icon.jsx";

import ArtistIcon from '@/assets/all-icons/artist-icon';
import ArtlistIcon from '@/assets/all-icons/artlist-icon';
import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useQuery from '@/hooks/useQuery';
import Text from '@/shared-components/text/components/Text';

import './styles/ProfileNavigation.css';

function ProfileNavigationTab({ to, tabName, tabIcon, tabCount, isFirst }) {
  const location = useLocation();
  const splittedUrl = location.pathname.split("/");
  const isUserPage = splittedUrl.length === 3 && splittedUrl[1] === "user";

  return (
    <>
      <button className={`profile-navigation__tab ${tabCount === 1 ? "profile-navigation__one-tab-link": ""}`}>
        <NavLink
          className={({ isActive }) => isActive || (isFirst && isUserPage) ? "profile-navigation__tab-link active" : "profile-navigation__tab-link"}
          to={to}
          aria-label={tabName}
          preventScrollReset
          state={location.state}
          replace
        >
          <div>
            {tabIcon}

            <Text className="profile-navigation__tab-name" paragraph extraSmall>
              {tabName}
            </Text>
          </div>
        </NavLink>
      </button>
    </>
  );
}

export function GalleryProfileNavigation({ userProfileInfo, internal }) {
  const navigate = useNavigate();
  // const navigationContainerRef = useRef(null);
  const { artworks, posts, artlists, artists } = userProfileInfo.data;

  const totalTabs = [
    artworks,
    posts,
    artlists,
    artists
  ].reduce((count, value) => count + (value ? 1 : 0), 0);

  const location = useLocation();

  useEffect(() => {
    const currentURL = location.pathname;
    const splittedUrl = currentURL.split("/");
    const isUserPage = splittedUrl.length === 3 && splittedUrl[1] === "user";

    const lastTag = splittedUrl.slice(-1)[0];

    let redirectUrl = "artworks";
    if(isUserPage || ["artworks", "board", "artlists", "artists", "drafts"].includes(lastTag)) {
      return;
    }

    if (userProfileInfo && artworks)
      redirectUrl = "artworks";
    else if (userProfileInfo && artists)
      redirectUrl = "artists";
    else if (userProfileInfo && artlists)
      redirectUrl = "artlists";
    else if (userProfileInfo && posts)
      redirectUrl = "board";
    navigate(redirectUrl, { replace: true, state: location.state?.from });
  }, [location.pathname]);

  return (
    <div className="profile-navigation__container">
      {(internal || artworks) ? <ProfileNavigationTab
        to="artworks"
        tabName="Artworks"
        tabIcon={<ArtworkIcon className="profile-navigation__tab-icon artworks" />}
        tabCount={totalTabs}
        isFirst={true}
      /> : ""}

      {(internal || artists) ? <ProfileNavigationTab
        to="artists"
        tabName="Artists"
        tabIcon={<ArtistIcon className="profile-navigation__tab-icon artists" />}
        tabCount={totalTabs}
      /> : ""}

      {(internal || artlists) ? <ProfileNavigationTab
        to="artlists"
        tabName="Artlists"
        tabIcon={<ArtlistIcon className="profile-navigation__tab-icon artlists" />}
        tabCount={totalTabs}
      /> : ""}

      {(internal || posts) ? <ProfileNavigationTab
        to="board"
        tabName="Board"
        tabIcon={<BoardIcon className="profile-navigation__tab-icon board" />}
        tabCount={totalTabs}
      /> : ""}
    </div>
  );

}

export function ArtistProfileNavigation({ userProfileInfo, internal }) {
  const navigate = useNavigate();
  const navigationContainerRef = useRef(null);

  const { artworks, collections, thoughts, ratings, artlists }= userProfileInfo.data;

  const tempTabCount = [
    artworks,
    collections,
    thoughts || ratings,
    artlists
  ].reduce((count, value) => count + (value ? 1 : 0), 0);

  const location = useLocation();
  const [firstPage, setFirstPage] = useState("");

  useEffect(() => {
    const currentURL = location.pathname;
    const splittedUrl = currentURL.split("/");
    const isUserPage = splittedUrl.length === 3 && splittedUrl[1] === "user";
    const lastTag = splittedUrl.slice(-1)[0];

    let redirectUrl = "artworks";

    if(isUserPage || ["artworks", "collection", "community", "artlists"].includes(lastTag))
    {
      if(artworks > 0) {
        setFirstPage("artworks");
      } else if(collections > 0) {
        setFirstPage("collection");
      }
      else if(thoughts > 0 || ratings > 0) {
        setFirstPage("community");
      }
      else if(artlists > 0) {
        setFirstPage("artlists");
      }
      return;
    }
    
    if (userProfileInfo && artworks) redirectUrl = "artworks";
    else if (userProfileInfo && collections) redirectUrl = "collection";
    else if (userProfileInfo && (thoughts || ratings)) redirectUrl = "community";
    else if (userProfileInfo && artlists) redirectUrl = "artlists";
    navigate(redirectUrl, { replace: true, state: location.state?.from });
  }, [location.pathname]);

  return (
    <div className="profile-navigation__container"  ref={navigationContainerRef}>
      {(internal || artworks) ? <ProfileNavigationTab
        to="artworks"
        tabName="Artworks"
        tabIcon={<ArtworkIcon className="profile-navigation__tab-icon artworks" />}
        tabCount={tempTabCount}
        isFirst={"artworks" === firstPage}
      /> : ""}

      {(internal || collections) ? <ProfileNavigationTab
        to="collection"
        tabName="Collection"
        tabIcon={<CollectionIcon className="profile-navigation__tab-icon collection" />}
        tabCount={tempTabCount}
        isFirst={"collection" === firstPage}
      /> : ""}

      {(internal || thoughts || ratings) ? <ProfileNavigationTab
        to="community"
        tabName="Community"
        tabIcon={<CommunityIcon className="profile-navigation__tab-icon community" />}
        tabCount={tempTabCount}
        isFirst={"community" === firstPage}
      /> : ""}

      {(internal || artlists) ? <ProfileNavigationTab
        to="artlists"
        tabName="Artlists"
        tabIcon={<ArtlistIcon className="profile-navigation__tab-icon artlists" />}
        tabCount={tempTabCount}
        isFirst={"artlists" === firstPage}
      /> : ""}
    </div>
  );
}

export function CollectorProfileNavigation({ userProfileInfo, internal }) {
  const navigate = useNavigate();
  const navigationContainerRef = useRef(null);

  const { collections, ratings, artlists } = userProfileInfo.data;

  const tempTabCount = [
    collections,
    ratings,
    artlists
  ].reduce((count, value) => count + (value ? 1 : 0), 0);

  const location = useLocation();
  const [firstPage, setFirstPage] = useState("");

  useEffect(() => {
    const currentURL = location.pathname;
    const splittedUrl = currentURL.split("/");
    const lastTag = currentURL.split("/").slice(-1)[0];
    const isUserPage = splittedUrl.length === 3 && splittedUrl[1] === "user";

    let redirectUrl = "collection";

    if (isUserPage || ["artworks", "collection", "community", "artlists"].includes(lastTag)) {
      if (collections > 0) {
        setFirstPage("collection");
      }
      else if (ratings > 0) {
        setFirstPage("community");
      }
      else if (artlists > 0) {
        setFirstPage("artlists");
      }
      return;
    }
    if (userProfileInfo && collections) redirectUrl = "collection";
    else if (userProfileInfo && ratings) redirectUrl = "community";
    else if (userProfileInfo && artlists) redirectUrl = "artlists";
    navigate(redirectUrl, { replace: true, state: location.state?.from });
  }, [location.pathname]);

  return (
    <div className="profile-navigation__container"  ref={navigationContainerRef}>
      {(internal || collections) ? <ProfileNavigationTab
        to="collection"
        tabName="Collection"
        tabIcon={<CollectionIcon className="profile-navigation__tab-icon collection" />}
        tabCount={tempTabCount}
        isFirst={"collection" === firstPage}
      /> : ""}

      {(internal || userProfileInfo.data.thoughts || ratings) ? <ProfileNavigationTab
        to="community"
        tabName="Community"
        tabIcon={<CommunityIcon className="profile-navigation__tab-icon community" />}
        tabCount={tempTabCount}
        isFirst={"community" === firstPage}
      /> : ""}

      {(internal || artlists) ? <ProfileNavigationTab
        to="artlists"
        tabName="Artlists"
        tabIcon={<ArtlistIcon className="profile-navigation__tab-icon artlists" />}
        tabCount={tempTabCount}
        isFirst={"artlists" === firstPage}
      /> : ""}
    </div>
  );
}

export default function ProfileNavigation({ fetchData }) {
  const { profileData, internal } = useContext(ProfileDataContext);
  const fetchUrl = `${config.apis.api.url}/user/userProfile/${profileData._id}`;
  const query = { user_type: fetchData.user_type };
  const { queryData: userProfileInfo } = useQuery(`${profileData._id}_userProfileInfo`, fetchUrl, query, {
    expiresIn: ["10", "minutes"]
  });

  switch(fetchData.user_type) {
  case "gallery": 
    return (
      <>
        {
          userProfileInfo && (
            <>
              <GalleryProfileNavigation userProfileInfo={userProfileInfo.data} internal={internal} />
            </>
          )
        }
      </>
    );
  case "artist":
    return (
      <>
        {userProfileInfo && (
          <>
            <ArtistProfileNavigation userProfileInfo={userProfileInfo.data} internal={internal} />
          </>
        )
        }
      </>
    );
  case "collector": 
    return (
      <>
        {
          userProfileInfo && (
            <>
              <CollectorProfileNavigation userProfileInfo={userProfileInfo.data} internal={internal} />
            </>
          )
        }
      </>
    );
  default:
    return (<></>);
  }
}